/**
 * Reversible, isomorphic obfuscation for values that must not appear in the
 * public API response in plain, directly readable form (e.g. product stock).
 *
 * This is deliberate obfuscation, not cryptography: any value the browser can
 * decode is inherently reversible. The goal is that an actor crawling the raw
 * response cannot *trivially* read the value, while the feature keeps working
 * unchanged. Avoids node `crypto` so it runs both server-side and in the browser.
 */

// Arbitrary, non-secret repeating key used to scramble the value before base64
// encoding so the token is not a recognizable string or number.
const XOR_KEY = [0x37, 0x91, 0x5b, 0xa2];

const toBase64 = (binary: string): string =>
  typeof btoa === 'function'
    ? btoa(binary)
    : Buffer.from(binary, 'binary').toString('base64');

const fromBase64 = (token: string): string =>
  typeof atob === 'function'
    ? atob(token)
    : Buffer.from(token, 'base64').toString('binary');

// XOR is its own inverse, so the same routine both scrambles and unscrambles.
const scramble = (input: string): string =>
  Array.from(input, (char, index) =>
    String.fromCharCode(char.charCodeAt(0) ^ XOR_KEY[index % XOR_KEY.length]),
  ).join('');

export const obfuscate = (value: string | number): string =>
  toBase64(scramble(String(value)));

export const deobfuscate = (token?: string): string | undefined => {
  if (!token) {
    return undefined;
  }

  try {
    return scramble(fromBase64(token));
  } catch {
    return undefined;
  }
};

export const deobfuscateNumber = (token?: string): number | undefined => {
  const value = deobfuscate(token);

  if (value === undefined || !/^\d+$/.test(value)) {
    return undefined;
  }

  return Number(value);
};
